/**
 * QuickLogTimeSheet — a focused timesheet creator used by the Status Log
 * "Add Status + Log Time" button on Proposal/Lead/RFP/Project detail panels
 * and the Meeting Notes "Save Note + Log Time" button.
 *
 * It POSTs to /api/timesheets with the linkage hardcoded to the record the
 * user was viewing. The user only edits the few free-form fields:
 *   - Date (defaults to today)
 *   - Hours
 *   - Billable (defaults to true)
 *   - Notes (pre-filled from the status_text the user just typed)
 *   - Section (only when the linked entity is a project)
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { toast } from 'sonner';
import api from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';
import { sortSections } from '@/lib/sectionSort';

// Strip HTML tags + decode the few entities that show up in our rich-text
// content so the timesheet notes start out clean. Users can still edit.
function htmlToText(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim();
}

export function QuickLogTimeSheet({
  open, onOpenChange,
  entityType,        // 'project' | 'proposal' | 'lead' | 'rfp'
  entityId,
  entityLabel,       // display string used in the sheet header
  initialNotes,      // pre-fill (status_text or meeting note content)
  sourceStatusLogId, // optional — links the resulting timesheet back to the
                     // status entry that spawned it (for the upcoming
                     // "Status -> Time" KPI widget)
  sourceSurface = 'status_log_log_time', // 'status_log_log_time' | 'meeting_note_log_time'
  onLogged,          // optional callback after success
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [hours, setHours] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [notes, setNotes] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);

  // Reset fields each time the sheet opens; fetch sections for projects.
  useEffect(() => {
    if (!open) return;
    setDate(today);
    setHours('');
    setIsBillable(true);
    setNotes(initialNotes || '');
    setSectionId('');
    if (entityType === 'project' && entityId) {
      api.get(`/projects/${entityId}/sections`)
        .then(r => setSections(r.data || []))
        .catch(() => setSections([]));
    } else {
      setSections([]);
    }
  }, [open, entityType, entityId, initialNotes, today]);

  const save = async () => {
    if (!date) return toast.error('Date is required');
    if (date > today) return toast.error('Cannot log time for a future date');
    const h = parseFloat(hours);
    if (!h || h <= 0) return toast.error('Hours must be greater than 0');
    if (!notes?.trim()) return toast.error('Notes are required');
    if (entityType === 'project' && !sectionId) return toast.error('Please select a section');
    if (h > 12 && !window.confirm(`You are logging ${h} hours for this day. Continue?`)) return;
    const payload = {
      date,
      hours: h,
      description: notes,
      is_billable: isBillable,
      source_surface: sourceSurface,
    };
    if (sourceStatusLogId) payload.source_status_log_id = sourceStatusLogId;
    if (entityType === 'project') {
      payload.project_id = entityId;
      payload.contract_section_id = sectionId;
    } else if (entityType === 'proposal') {
      payload.proposal_id = entityId;
    } else if (entityType === 'lead') {
      payload.lead_id = entityId;
    } else if (entityType === 'rfp') {
      payload.rfp_id = entityId;
    }
    try {
      setSaving(true);
      await api.post('/timesheets', payload);
      toast.success('Time logged');
      onOpenChange(false);
      onLogged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log time');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="quick-log-time-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading">Log Time</SheetTitle>
          <SheetDescription>
            Linked to <span className="text-foreground font-medium">{entityLabel}</span>
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-3 mt-4">
          <div>
            <Label className="text-xs text-muted-foreground">Date *</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-background border-border" data-testid="quick-log-date" />
          </div>
          {entityType === 'project' && (
            <div>
              <Label className="text-xs text-muted-foreground">Section *</Label>
              <Select value={sectionId || 'none'} onValueChange={v => setSectionId(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-background border-border" data-testid="quick-log-section"><SelectValue placeholder="Select section" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">Select section...</SelectItem>
                  {sortSections(sections).map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.section_number ? `${s.section_number} - ` : ''}{s.section_name}{s.source_tag && s.source_tag !== 'Original Contract' ? ` [${s.source_tag}]` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-muted-foreground">Hours *</Label>
            <Input type="number" step="0.25" min="0" max="24" value={hours} onChange={e => setHours(e.target.value)} className="bg-background border-border" placeholder="0.0" data-testid="quick-log-hours" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Notes *</Label>
            <RichTextEditor value={notes} onChange={v => setNotes(v)} placeholder="What did you work on?" data-testid="quick-log-notes" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="quick-billable" checked={isBillable} onCheckedChange={v => setIsBillable(!!v)} data-testid="quick-log-billable" />
            <Label htmlFor="quick-billable" className="text-xs text-muted-foreground cursor-pointer">Billable</Label>
          </div>
          {!isBillable && (
            <p className="text-xs text-amber-400/80 bg-amber-500/5 border border-amber-500/15 rounded px-2.5 py-1.5 leading-relaxed">
              Only mark as non-billable for breaks or time the company should not pay you for. All internal and external work should remain billable.
            </p>
          )}
          <Button onClick={save} disabled={saving} className="w-full bg-primary text-primary-foreground mt-2" data-testid="quick-log-save-btn">
            {saving ? 'Saving...' : 'Log Time'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Pure helper exposed for callers that want the same stripped-notes value.
export { htmlToText };
